import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Spinner,
  Tag,
  Text
} from '@chakra-ui/react';
import { useWeb3React } from 'hooks/web3';
import { FC, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useConnection } from 'store/connection';
import { ConnectWallet } from 'components/manage/ConnectWallet';
import { usePrograms } from 'hooks/query/usePrograms';
import { FundingFormWrapper } from 'components/manage/FundingFormWrapper';
import { TransferFormWrapper } from 'components/manage/TransferFormWrapper';
import { PowerSwitchFormWrapper } from 'components/manage/PowerswitchFormWrapper';
import { MaintenanceFormWrapper } from 'components/manage/MaintenanceFormWrapper';
import { useRewardProgramState } from 'hooks/query/useRewardProgramState';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { mapPowerSwitchStatusToDescription } from 'api/blockchain/rewardProgram';
import getColorScheme from 'helpers/getColorScheme';
import { ImBlocked } from 'react-icons/im';
import { RewardProgramSummary } from 'components/manage/RewardProgramSummary';
import { queryClient } from 'index';
import { Queries } from 'types/enum';

const ManageDetail: FC = () => {
  const { account, chainId } = useWeb3React();
  const { tried } = useConnection();
  const { data, isLoading } = usePrograms();
  const { rewardProgramAddress } =
    useParams<{ rewardProgramAddress: string }>();

  const program = useMemo(() => {
    if (data) {
      let searchProgram = data.find(
        (d) =>
          d.rewardProgram.address.toLowerCase() ===
          rewardProgramAddress.toLowerCase()
      );

      if (searchProgram) {
        queryClient.refetchQueries([
          Queries.REWARD_PROGRAMS,
          rewardProgramAddress,
          chainId
        ]);

        return searchProgram;
      }
    }
  }, [data, chainId, rewardProgramAddress]);

  const { data: programStateData } = useRewardProgramState(
    program?.rewardProgram
  );

  if (!tried) {
    return <Spinner />;
  }
  if (isLoading) {
    return <Spinner />;
  }
  if (!program) {
    return <Text>Failed to load program at this address</Text>;
  }
  if (!account) {
    return (
      <ConnectWallet headingText='Connect your wallet to manage your Reward Program.' />
    );
  }

  const isOwner =
    account.toLowerCase() === program.rewardProgram.owner.toLowerCase();
  const isPowerSwitchController =
    account.toLowerCase() ===
    program.rewardProgram.powerSwitchController.toLowerCase();
  const noAccess = (
    <>
      <Text color='red.500' textAlign={'center'} fontSize='2xl' my={3}>
        <Icon as={ImBlocked} />
      </Text>
      <Text color='gray.200' textAlign={'center'}>
        These controls are available to the{' '}
        {isOwner ? 'Power Switch' : 'Aludel'} owner (
        {isOwner
          ? program.rewardProgram.powerSwitchController.toLowerCase()
          : program.rewardProgram.owner.toLowerCase()}
        ) only.
      </Text>
    </>
  );

  if (!isOwner && !isPowerSwitchController) {
    return (
      <Text color='gray.200'>
        You are not the owner or Power Switch controller for this program.
      </Text>
    );
  }

  return (
    <Box p={[5, null, null, 10]} bg='gray.800' borderRadius='xl'>
      <Box
        mb={4}
        top={[0, 0, 0, 5]}
        position={['relative', null, null, 'absolute']}
      >
        <Button
          as={Link}
          to={'/manage'}
          variant='ghost'
          backgroundColor={['none', null, null, 'whiteAlpha.200']}
          _hover={{ bg: ['whiteAlpha.200', null, null, 'gray.800'] }}
        >
          <HStack>
            <IoIosArrowRoundBack fontSize='32px' />
            <Text>Manage List</Text>
          </HStack>
        </Button>
      </Box>
      <Box
        py={8}
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
      >
        <Heading size='xl' textAlign='center' mb={2}>
          Manage {program.rewardProgram.name}
        </Heading>
        {(programStateData === null || programStateData === undefined) && (
          <Skeleton width='100px' height='24px' borderRadius={8} />
        )}
        {(programStateData || programStateData === 0) && (
          <Tag colorScheme={getColorScheme(programStateData)}>
            {mapPowerSwitchStatusToDescription[programStateData]}
          </Tag>
        )}
      </Box>
      {/* TODO: Add UI if not owner or power switch controller */}
      <Accordion
        defaultIndex={[isOwner ? 1 : 3]}
        sx={{ border: 'transparent' }}
        allowMultiple
      >
        {[
          'Addresses',
          'Program Funding',
          'Program Management',
          'Power Controller: Management',
          'Power'
        ].map((i, k) => (
          <AccordionItem
            key={i}
            mb={4}
            borderRadius={10}
            borderWidth={2}
            borderStyle='solid'
            borderColor='gray.600'
          >
            <h2>
              <AccordionButton
                sx={{
                  background: 'gray.700',
                  _expanded: {
                    bg: 'gray.600',
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0
                  },
                  _hover: { bg: 'gray.600' },
                  padding: 4,
                  borderRadius: 8
                }}
              >
                <Box flex='1' textAlign='left'>
                  <Text fontSize='lg'>{i}</Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              {k === 0 ? <RewardProgramSummary program={program} /> : ''}
              {k === 1 ? (
                isOwner ? (
                  <FundingFormWrapper
                    program={program}
                    programState={programStateData}
                  />
                ) : (
                  noAccess
                )
              ) : (
                ''
              )}
              {k === 2 ? (
                isOwner ? (
                  <TransferFormWrapper
                    program={program}
                    programState={programStateData}
                  />
                ) : (
                  noAccess
                )
              ) : (
                ''
              )}
              {k === 3 ? (
                isPowerSwitchController ? (
                  <PowerSwitchFormWrapper
                    program={program}
                    programState={programStateData}
                  />
                ) : (
                  noAccess
                )
              ) : (
                ''
              )}
              {k === 4 ? (
                isPowerSwitchController ? (
                  <MaintenanceFormWrapper
                    program={program}
                    programState={programStateData}
                  />
                ) : (
                  noAccess
                )
              ) : (
                ''
              )}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
};

export default ManageDetail;
